import { Col, Form, Row } from "react-bootstrap";
import QueryForm from "./QueryForm";
import { useMemo, useState } from "react";
import CenteredModal from "components/core/basic/CenteredModal";
import QueryCard from "./ReportTypeCard";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { DASHBOARD_LINKS } from "utils/dashboardLinks";
import { AMC_QUERY_EXPLORER_BREADCRUMB_SPEC, CANNED_QUERIES, FEATURED_REPORTS } from "./utils";
import Pagination from "./Pagination";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { successToast, errorToast } from "utils/toasts";
import api from "utils/api";
import { useAppSelector } from "redux/hooks";
import Loading from "components/core/blocks/Loading";
import { useAMCQueryData } from "./hooks/useAMCQueryData";
import { useEnabledModules, useModuleEnabled } from "redux/selectors/modules";
import { Redirect } from "react-router-dom";
import { Button } from "components/core/basic/Button";
import { useAMCInstanceDataSouces } from "./hooks/useAMCInstanceDataSources";
import useQueryParams from "hooks/useQueryParams";
import Tabs from "components/core/basic/Tabs";
import AMCSubscriptionsTable from "./AMCSubscriptionsTable";

const PAGE_SIZE = 4;

const AMCDashboard = () => {
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [showCustomReportForm, setShowCustomReportForm] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [subscribedQuerySearch, setSubscribedQuerySearch] = useState("");
  const [availableQuerySearch, setAvailableQuerySearch] = useState("");
  const [currentSubscribedPage, setCurrentSubscribedPage] = useState(1);
  const [currentAvailablePage, setCurrentAvailablePage] = useState(1);
  const [querySection, setQuerySection] = useQueryParams("section", "all");
  const [selectedQueryToExpand, setSelectedQueryToExpand] = useState(null);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const queryClient = useQueryClient();
  const hasAMCModule = useModuleEnabled("AMC");
  const enabledModules = useEnabledModules();

  const { data: subscribedQueriesData, isLoading } = useAMCQueryData();
  const { data: availableDataSources } = useAMCInstanceDataSouces();

  const subscribeHandler = useMutation({
    mutationFn: async (queryData) => {
      const { name, frequency, parameter_list } = queryData;

      const parameters = {};

      for (const param of parameter_list) {
        if (param in queryData) {
          parameters[param] = queryData[param];
        }
      }

      if (selectedQuery.subscribed) {
        const updateData = { frequency: frequency.value, parameters: parameters };

        await api.patch(`/amc/subscriptions/${selectedQuery.id}/`, updateData);
      } else {
        const subscriptionData = {
          enabled: true,
          query_type: name,
          marketplace: marketPlace.marketPlace,
          parameters: parameters,
          frequency: frequency.value,
        };
        await api.post("/amc/subscriptions/", subscriptionData);
      }
    },

    select: (res) => res.data,
    enabled: true,
    default: [],

    onSuccess: () => {
      successToast({
        message:
          selectedQuery?.subscribed ? "Successfully updated query parameters" : "Successfully subscribed to query",
      });
      setSelectedQuery(null);
      queryClient.invalidateQueries({
        queryKey: ["subscribedAMCQueries"],
      });
    },

    onError: (error) => {
      errorToast({
        message: "Failed to subscribe to query",
        error,
      });
    },
  });

  const unsubscribeHandler = useMutation({
    mutationFn: async (subscriptionId) => {
      await api.remove(`/amc/subscriptions/${subscriptionId}`);
    },

    select: (res) => res.data,
    enabled: true,
    default: [],

    onSuccess: () => {
      successToast({
        message: "Successfully unsubscribed from query",
      });
      queryClient.invalidateQueries({
        queryKey: ["subscribedAMCQueries"],
      });
    },

    onError: (error) => {
      errorToast({
        message: "Failed to unsubscribe from query",
        error,
      });
    },
  });

  const toggleStatusHandler = useMutation({
    mutationFn: async (queryStatusData) => {
      const { subscriptionId, enabled } = queryStatusData;

      await api.patch(`/amc/subscriptions/${subscriptionId}/`, {
        enabled,
      });
    },

    select: (res) => res.data,
    enabled: true,
    default: [],

    onSuccess: (data, variables) => {
      successToast({
        message: `Successfully ${variables.enabled ? "enabled" : "disabled"} ${variables.queryName} query subscription`,
      });
      queryClient.invalidateQueries({
        queryKey: ["subscribedAMCQueries"],
      });
    },

    onError: (error, variables) => {
      const displayName = CANNED_QUERIES.find((query) => {
        return query.name === variables?.queryName;
      })?.display_name;

      errorToast({
        message: `Failed to ${variables.enabled ? "enabled" : "disabled"} ${displayName} query`,
        error,
      });
    },
  });

  const breadCrumbItems = [
    {
      name: "AMC Dashboard",
      href: "/user/amc/dashboard",
      unsorted: true,
      dropdownLinkOptions: DASHBOARD_LINKS,
      dropdownSubtitle: "Reports",
    },
  ];

  if (!breadCrumbItems[0].dropdownLinkOptions?.find((o) => o.name === AMC_QUERY_EXPLORER_BREADCRUMB_SPEC.name)) {
    breadCrumbItems[0].dropdownLinkOptions.push(AMC_QUERY_EXPLORER_BREADCRUMB_SPEC);
  }

  const subscribedQueries = useMemo(() => {
    return (
      [...CANNED_QUERIES, ...FEATURED_REPORTS].filter((query) =>
        subscribedQueriesData?.find((subscribedQuery) => subscribedQuery.query_type === query.name),
      ) ?? []
    ).map((query) => {
      const querySubscriptionSettings = subscribedQueriesData?.find(
        (subscribedQuery) => subscribedQuery.query_type === query.name,
      );

      return { ...query, ...querySubscriptionSettings, subscribed: true };
    });
  }, [subscribedQueriesData]);

  const availableQueries = useMemo(() => {
    return (
      CANNED_QUERIES.filter((query) => {
        const { modules } = query;
        if (modules) {
          const module = enabledModules.find((module) => modules.includes(module.name));
          if (!module) {
            return false;
          }
        }
        return !subscribedQueriesData?.find((subscribedQuery) => subscribedQuery.query_type === query.name);
      }) ?? []
    );
  }, [subscribedQueriesData, enabledModules]);

  const filteredSubscribedQueries = useMemo(() => {
    return subscribedQueries?.filter((query) => {
      if (subscribedQuerySearch) {
        const { name, description } = query;
        return `${name} ${description}`.toLowerCase().includes(subscribedQuerySearch.toLowerCase());
      }
      return true;
    });
  }, [subscribedQuerySearch, subscribedQueries]);

  const filteredAvailableQueries = useMemo(() => {
    return availableQueries?.filter((query) => {
      if (availableQuerySearch) {
        const { name, description } = query;
        return `${name} ${description}`.toLowerCase().includes(availableQuerySearch.toLowerCase());
      }
      return true;
    });
  }, [availableQuerySearch, availableQueries]);

  const paginatedSubscribedQueries = useMemo(() => {
    return filteredSubscribedQueries?.slice(
      (currentSubscribedPage - 1) * PAGE_SIZE,
      (currentSubscribedPage - 1) * PAGE_SIZE + PAGE_SIZE,
    );
  }, [filteredSubscribedQueries, currentSubscribedPage]);

  const paginatedAvailableQueries = useMemo(() => {
    return filteredAvailableQueries.slice(
      (currentAvailablePage - 1) * PAGE_SIZE,
      (currentAvailablePage - 1) * PAGE_SIZE + PAGE_SIZE,
    );
  }, [filteredAvailableQueries, currentAvailablePage]);

  if (!hasAMCModule) {
    return <Redirect to="/user/dashboard" />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-0">
          <BreadcrumbDates items={breadCrumbItems} hideDateSelector={true} />
        </Col>
      </Row>
      <Row style={{ padding: "20px 150px" }}>
        <Col xs={12}>
          <h1 className="text-center" style={{ fontWeight: 600, fontSize: "24px" }}>
            Amazon Marketing Cloud
          </h1>
        </Col>
      </Row>
      {isLoading ?
        <Loading fullPage={true} />
      : <>
          <Row>
            <Col xs={12} lg={{ span: 8, offset: 2 }}>
              <Tabs
                tabs={[
                  {
                    label: "Reports",
                    key: "all",
                  },
                  { label: "Subscriptions", key: "subscriptions" },
                ]}
                selectedTab={querySection}
                onTabChange={setQuerySection}
              />
              {querySection === "all" && (
                <>
                  <h3 className="m-0 py-2 px-5 fs-2" style={{ fontWeight: 600 }}>
                    {FEATURED_REPORTS.length} Featured Report{FEATURED_REPORTS.LENGTH !== 1 ? "s" : ""}
                  </h3>
                  <Row>
                    {FEATURED_REPORTS.map((query, index) => {
                      return (
                        <QueryCard
                          query={query}
                          key={index}
                          subscribed={false}
                          featured={true}
                          availableSources={availableDataSources}
                          onViewSubscriptions={() => {
                            if (["PATH_TO_PURCHASE_GROUPS_INTERNAL"].includes(query.name)) {
                              setSelectedQueryToExpand(query.name);
                              setQuerySection("subscriptions");
                            }
                          }}
                          onSelect={() => {
                            if (["PATH_TO_PURCHASE_GROUPS_INTERNAL"].includes(query.name)) {
                              setSelectedQuery({ ...query, subscribed: false, parameters: {} });
                              setShowQueryForm(true);
                            }
                          }}
                        />
                      );
                    })}
                  </Row>
                </>
              )}
            </Col>
          </Row>
          {querySection === "all" && (
            <Row>
              <Col xs={12} lg={{ span: 8, offset: 2 }} className="pb-5">
                <h3 className="m-0 py-2 px-5 fs-2" style={{ fontWeight: 600 }}>
                  {subscribedQueries.length} Subscribed Report{subscribedQueries.length !== 1 ? "s" : ""}
                </h3>
                <Row>
                  <Col xs={12} md={6} xl={4} className="py-3 px-5">
                    <Form.Control
                      type="search"
                      placeholder={"Search for a subscribed report..."}
                      onChange={(e) => setSubscribedQuerySearch(e.target.value)}
                      style={{ borderRadius: "5px" }}
                    />
                  </Col>
                  {filteredSubscribedQueries.length > PAGE_SIZE && (
                    <Col xs={12} md={6} xl={8} className={"d-flex justify-content-end"}>
                      <Pagination
                        data={filteredSubscribedQueries}
                        pageSize={PAGE_SIZE}
                        currentPage={currentSubscribedPage}
                        setCurrentPage={setCurrentSubscribedPage}
                      />
                    </Col>
                  )}
                </Row>
                <Row>
                  {paginatedSubscribedQueries.map((query, index) => {
                    return (
                      <QueryCard
                        query={query}
                        key={index}
                        index={index}
                        onSelect={() => {
                          setSelectedQuery({ ...query, subscribed: false, parameters: {} });
                          setShowQueryForm(true);
                        }}
                        onViewSubscriptions={() => {
                          setSelectedQueryToExpand(query.name);
                          setQuerySection("subscriptions");
                        }}
                        toggleStatus={(value) => {
                          toggleStatusHandler.mutate({ queryName: query.name, enabled: value });
                        }}
                        subscribed={true}
                        availableSources={availableDataSources}
                      />
                    );
                  })}
                </Row>
              </Col>

              <Col xs={12} lg={{ span: 8, offset: 2 }}>
                <div className="d-flex justify-content-between">
                  <h3 className="m-0 py-2 ps-5 fs-2" style={{ fontWeight: 600 }}>
                    {availableQueries.length} Available Report{availableQueries.length !== 1 ? "s" : ""}
                  </h3>
                  <CenteredModal
                    show={showCustomReportForm}
                    closeModal={() => setShowCustomReportForm(false)}
                    title="Request a Custom Report"
                    dialogClassName="modal-60w"
                    content={
                      <Row className="pb-5 d-flex justify-content-center gap-3">
                        <p className="text-center">
                          To have a custom report created, please contact your customer success representative and
                          provide a description of the custom report you would like to have generated.
                        </p>
                        <Button onPress={() => setShowCustomReportForm(false)}>Continue</Button>
                      </Row>
                    }
                  />
                  <Button onPress={() => setShowCustomReportForm(true)}>Request a Custom Report</Button>
                </div>
                <Row>
                  <Col xs={12} md={6} xl={4} className="py-3 px-5">
                    <Form.Control
                      type="search"
                      placeholder={"Search for an available report..."}
                      onChange={(e) => {
                        setAvailableQuerySearch(e.target.value);
                      }}
                      style={{ borderRadius: "5px" }}
                    />
                  </Col>
                  {filteredAvailableQueries.length > PAGE_SIZE && (
                    <Col xs={12} md={6} xl={8} className={"d-flex justify-content-end"}>
                      <Pagination
                        data={filteredAvailableQueries}
                        pageSize={PAGE_SIZE}
                        currentPage={currentAvailablePage}
                        setCurrentPage={setCurrentAvailablePage}
                      />
                    </Col>
                  )}
                </Row>
                <Row>
                  {paginatedAvailableQueries.map((query, index) => {
                    return (
                      <QueryCard
                        query={query}
                        key={index}
                        onSelect={() => {
                          setSelectedQuery(query);
                          setShowQueryForm(true);
                        }}
                        subscribed={false}
                        availableSources={availableDataSources}
                      />
                    );
                  })}
                </Row>
              </Col>
            </Row>
          )}
          {querySection === "subscriptions" ?
            <AMCSubscriptionsTable
              selectedQueryToExpand={selectedQueryToExpand}
              setSelectedQueryToExpand={setSelectedQueryToExpand}
              setSelectedQuery={setSelectedQuery}
              setShowQueryForm={setShowQueryForm}
              unsubscribeHandler={unsubscribeHandler}
              toggleStatusHandler={toggleStatusHandler}
            />
          : null}
        </>
      }
      <CenteredModal
        show={showQueryForm}
        closeModal={() => setShowQueryForm(false)}
        title={
          selectedQuery?.subscribed ?
            `Update ${selectedQuery?.display_name} Query`
          : `Subscribe to ${selectedQuery?.display_name} Query`
        }
        dialogClassName="modal-60w"
        content={
          <QueryForm
            setShowModal={(value) => setShowQueryForm(value)}
            query={selectedQuery}
            onFormSubmit={(query) => {
              subscribeHandler.mutate({ ...selectedQuery, ...query });
            }}
          />
        }
      />
    </div>
  );
};

export default AMCDashboard;
